import { useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, PlayCircle, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  getMateriaisControllerListarQueryKey,
  useMateriaisControllerAtualizar,
  useMateriaisControllerCriar,
  useMateriaisControllerEnviarArquivo,
} from '@/api/generated/materiais/materiais';
import type { MeuMaterialResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

type Tipo = 'VIDEO' | 'PDF';

/**
 * Envio e edição de material.
 *
 * ⚠️ O upload do PDF é uma chamada SEPARADA (`POST /v1/materiais/arquivo`), e
 * acontece antes de salvar. É o que evita o parceiro reescrever título e
 * descrição toda vez que o arquivo é recusado por tamanho ou formato.
 */
export function MaterialFormDialogo({
  aberto,
  aoFechar,
  material,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  material?: MeuMaterialResponse;
}) {
  const queryClient = useQueryClient();
  const criar = useMateriaisControllerCriar();
  const atualizar = useMateriaisControllerAtualizar();
  const enviarArquivo = useMateriaisControllerEnviarArquivo();

  const [tipo, setTipo] = useState<Tipo>((material?.tipo as Tipo) ?? 'VIDEO');
  const [titulo, setTitulo] = useState(material?.titulo ?? '');
  const [descricao, setDescricao] = useState(material?.descricao ?? '');
  // `urlOriginal`, não `url`: o servidor devolve o embed normalizado em `url`, e
  // reabrir o formulário com ele faria o campo "mudar sozinho" a cada edição.
  const [url, setUrl] = useState(material?.urlOriginal ?? '');
  const [arquivoId, setArquivoId] = useState(material?.arquivoId ?? '');
  const [nomeDoArquivo, setNomeDoArquivo] = useState('');
  const [erros, setErros] = useState<Record<string, string[]>>({});

  const inputDeArquivo = useRef<HTMLInputElement>(null);
  const salvando = criar.isPending || atualizar.isPending;

  async function selecionarArquivo(arquivo: File): Promise<void> {
    try {
      const resposta = await enviarArquivo.mutateAsync({ data: { arquivo } });
      setArquivoId(resposta.arquivoId);
      setNomeDoArquivo(resposta.nomeOriginal);
      setErros((atual) => ({ ...atual, arquivoId: [] }));
    } catch (erro) {
      // O servidor manda a regra na mensagem ("envie PDF", "o limite é 10 MB").
      // Repetir a regra aqui criaria dois textos para divergir.
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível enviar o arquivo');
    }
  }

  function salvar(): void {
    setErros({});

    const dados = {
      titulo: titulo.trim(),
      descricao: descricao.trim() || null,
      tipo,
      url: tipo === 'VIDEO' ? url.trim() : null,
      arquivoId: tipo === 'PDF' ? arquivoId || null : null,
    };

    const aoDarCerto = () => {
      void queryClient.invalidateQueries({ queryKey: getMateriaisControllerListarQueryKey() });
      toast.success(material ? 'Material atualizado e reenviado para aprovação' : 'Material enviado para aprovação');
      aoFechar();
    };

    const aoFalhar = (erro: unknown) => {
      if (erro instanceof ApiError && erro.detalhes) {
        setErros(erro.detalhes);
        return;
      }
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível salvar');
    };

    if (material) {
      atualizar.mutate({ id: material.id, data: dados }, { onSuccess: aoDarCerto, onError: aoFalhar });
    } else {
      criar.mutate({ data: dados }, { onSuccess: aoDarCerto, onError: aoFalhar });
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{material ? 'Editar material' : 'Novo material'}</DialogTitle>
          <DialogDescription>
            {material?.status === 'REJEITADO'
              ? 'Ao salvar, o material volta para a fila de aprovação.'
              : 'Todo material passa pela aprovação da equipe antes de aparecer na plataforma.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 p-1 bg-brand/10 rounded-lg">
            {(
              [
                ['VIDEO', 'Vídeo', PlayCircle],
                ['PDF', 'PDF', FileText],
              ] as const
            ).map(([valor, rotulo, Icone]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setTipo(valor)}
                className={cn(
                  'flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all',
                  tipo === valor
                    ? 'bg-brand text-brand-foreground shadow-sm'
                    : 'text-brand hover:bg-brand/10',
                )}
              >
                <Icone className="w-4 h-4" />
                {rotulo}
              </button>
            ))}
          </div>

          <Campo rotulo="Título" erro={erros.titulo}>
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} />
          </Campo>

          <Campo rotulo="Descrição" erro={erros.descricao}>
            <Textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={3}
              maxLength={2000}
            />
          </Campo>

          {tipo === 'VIDEO' ? (
            <Campo rotulo="URL do vídeo" erro={erros.url}>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://youtu.be/... ou https://vimeo.com/..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Aceitamos YouTube e Vimeo.
              </p>
            </Campo>
          ) : (
            <Campo rotulo="Arquivo PDF" erro={erros.arquivoId}>
              <input
                ref={inputDeArquivo}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const arquivo = e.target.files?.[0];
                  if (arquivo) void selecionarArquivo(arquivo);
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start gap-2"
                disabled={enviarArquivo.isPending}
                onClick={() => inputDeArquivo.current?.click()}
              >
                {enviarArquivo.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {nomeDoArquivo || (arquivoId ? 'PDF já enviado — trocar' : 'Escolher PDF')}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">PDF de até 10 MB.</p>
            </Campo>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            onClick={salvar}
            disabled={salvando}
            className="bg-brand text-brand-foreground hover:bg-brand/90"
          >
            {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
            {material ? 'Salvar e reenviar' : 'Enviar para aprovação'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  rotulo,
  erro,
  children,
}: {
  rotulo: string;
  erro?: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{rotulo}</Label>
      {children}
      {erro && erro.length > 0 && <p className="text-xs text-destructive">{erro[0]}</p>}
    </div>
  );
}
